// UsersOverview — list 15 learners, show per-learner hide progress.
//
// Day 14b decision: pin hiding is ruifang-only this event, so we drop
// the region picker and pin progress always counts ruifang pins. The
// page becomes the single source of truth for "how many learners have
// pins ready?" and the map page no longer needs a counter.

import {
  Hairline,
  Han,
  HUD,
  MonoData,
  MonoLabel,
  T,
} from "@goodbye/ui";
import { useMemo } from "react";
import { useAdminSyncCtx } from "../context";
import { useEventConfig } from "../hooks/useEventConfig";
import { usePersistentState } from "../hooks/usePersistentState";

const HIDE_REGION_ID = "ruifang";

export function UsersOverview() {
  const { users, markers, audios, loading } = useAdminSyncCtx();
  const { cohortId: currentCohortId } = useEventConfig();

  const [cohortFilter, setCohortFilter] = usePersistentState<string>(
    "admin-cohortFilter",
    "all",
  );

  const availableCohorts = useMemo(() => {
    const ids = new Set<string>();
    for (const u of users) {
      if (u.role !== "worker") ids.add(u.cohortId ?? "2025");
    }
    return Array.from(ids).sort();
  }, [users]);

  const cohortLearners = useMemo(
    () =>
      users.filter(
        (u) =>
          u.role !== "worker" &&
          (cohortFilter === "all" || (u.cohortId ?? "2025") === cohortFilter),
      ),
    [users, cohortFilter],
  );

  const activeCohort = cohortFilter === "all" ? currentCohortId : cohortFilter;

  const placed = markers.filter((m) => {
    if (m.regionId !== HIDE_REGION_ID) return false;
    if (cohortFilter === "all") return true;
    return cohortLearners.some((u) => u.id === m.userId);
  });
  const releasedAudios = audios.filter((a) =>
    cohortFilter === "all"
      ? a.released
      : (a.cohortReleased?.[activeCohort] ?? a.released),
  ).length;
  const totalPinsPlaced = placed.reduce((sum, m) => sum + m.pins.length, 0);
  const totalPinsFound = placed.reduce(
    (sum, m) => sum + m.pins.filter((p) => p.foundAt != null).length,
    0,
  );

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        // 底部留白移到實際裝列表的內容層(見下方),這裡不再墊(flex column
        // 末端的 paddingBottom 不可靠,最後幾個學員會被 fixed AdminNav 蓋住)。
      }}
    >
      <HUD label="工 作 端" />

      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <Han size={20} color={T.ink900}>{cohortLearners.length} 位學員</Han>
        <Han size={13} color={T.ink500}>
          瑞芳市區 · 已藏 {totalPinsPlaced}
        </Han>
        {totalPinsPlaced > 0 && (
          <Han size={11} color={T.ink500}>
            · 已找到 {totalPinsFound} / {totalPinsPlaced}
          </Han>
        )}
        <Han size={11} color={T.ink350}>
          音檔已釋出 {releasedAudios} / {audios.length} 段。
        </Han>
        {availableCohorts.length > 1 && (
          <div style={{ display: "flex", gap: 0, marginTop: 4 }}>
            {[{ id: "all", label: "全 部" }, ...availableCohorts.map((c) => ({ id: c, label: `${c} 屆` }))].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setCohortFilter(id)}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: `${cohortFilter === id ? 2 : 1}px solid ${cohortFilter === id ? T.ink900 : T.ink200}`,
                  padding: "12px 0",
                  marginRight: 20,
                  cursor: "pointer",
                  fontFamily: T.han,
                  fontSize: 13,
                  color: cohortFilter === id ? T.ink900 : T.ink500,
                  minHeight: 44,
                }}
              >
                {label}
                {id !== "all" && id === currentCohortId && (
                  <span style={{ color: T.ink500, fontSize: 11, marginLeft: 4 }}>· 本屆</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <Hairline style={{ margin: "8px 32px 0" }} />

      <div
        style={{
          padding: "0 32px",
          // 2026-05-30:墊出 fixed AdminNav 高度,讓最後幾位學員完整露出、
          // 不被底部 tab bar 蓋住(同 RegionsOpen 修法)。
          paddingBottom: "calc(120px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        {loading && <Han size={13} color={T.ink350}>連線中…</Han>}
        {cohortLearners.map((u) => {
          const m =
            markers.find(
              (mm) => mm.userId === u.id && mm.regionId === HIDE_REGION_ID,
            ) ?? null;
          return (
            <div
              key={u.id}
              style={{
                display: "grid",
                gridTemplateColumns: "34px 1fr auto",
                alignItems: "center",
                gap: 10,
                padding: "14px 0",
                borderBottom: `1px solid ${T.ink100}`,
              }}
            >
              <MonoData size={12} color={T.ink500}>
                {u.no}
              </MonoData>
              <Han size={15} color={T.ink900}>{u.name}</Han>
              {(() => {
                const pins = m?.pins ?? [];
                const total = pins.length;
                const found = pins.filter((p) => p.foundAt != null).length;
                const allFound = total > 0 && found === total;
                const placedByName = m?.placedBy?.split("@")[0] ?? "";
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 2,
                    }}
                  >
                    <MonoLabel
                      size={11}
                      color={
                        total === 0
                          ? T.ink350
                          : allFound
                            ? T.ink500
                            : T.ink900
                      }
                    >
                      · {found} / {total}
                      {allFound && " · 完"}
                    </MonoLabel>
                    {placedByName && (
                      <MonoData size={11} color={T.ink350}>
                        {placedByName}
                      </MonoData>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

    </div>
  );
}
